import * as STD from '@emersya/mpa-std';
import * as CM  from '@emersya/mpa-components-manager';

import { NS as ModalsZoneNS } from '../ModalsZone/ModalsZone';
import { NS as LoaderNS }     from '../Loader/Loader';

import './Application.scss';

export interface IApplicationComponentConfiguration extends CM.DOMComponent.IConfiguration {
};
export type TMessagesListenerComponent = CM.Component.TAny & {
	readonly  onMessage: (event: MessageEvent) => Promise<void> | void;
};

export class ApplicationComponent<
	TComponent extends ApplicationComponent<TComponent, TConfiguration>,
	TConfiguration extends IApplicationComponentConfiguration
> extends CM.DOMComponent<
	HTMLDivElement,
	TComponent,
	TConfiguration
> {
	static count = 0;
	static managerManifest: CM.Manifest.ManagerManifest;
	static readonly parameters = STD.net.URL_parse(location).parameters;

	readonly parameters = ApplicationComponent.parameters;
	component_DOM: HTMLDivElement;
	loader: LoaderNS.Component | null = null;
	currentlyHovered: null | {
		node: Node;
		component: CM.DOMComponent<any, any, any> | null;
	} = null;
	messagesListeners: WeakMap<Window | MessagePort | ServiceWorker, TMessagesListenerComponent> = new WeakMap;
	modals!: ModalsZoneNS.Component;

	disablePointerEvents() {
		this.component_DOM.style.pointerEvents = 'none';
	};

	enablePointerEvents() {
		this.component_DOM.style.pointerEvents = '';
	};

	disablePointerSelection() {
		this.component_DOM.style.userSelect = 'none';
	};

	enablePointerSelection() {
		this.component_DOM.style.userSelect = '';
	};

	protected override async component_finalize() {
		await super.component_finalize();
		STD.translationsManager.update(document.body);
	};

	private onMouseMove(nativeEvent: MouseEvent) {
		const node = <HTMLElement>nativeEvent.target;
		if (!node) {
			return;
		}
		if (this.component_DOM.contains(node)) {
			const hoveredComponent = CM.DOMComponent.getFromDOM(node);
			if (this.currentlyHovered) {
				this.currentlyHovered.component = hoveredComponent;
				this.currentlyHovered.node = node;
			} else {
				this.currentlyHovered = {
					component: hoveredComponent,
					node: node
				};
			}
		} else {
			this.currentlyHovered = null;
		}
	};

	private onMouseOut(nativeEvent: MouseEvent) {
		if (!nativeEvent.relatedTarget) {
			this.currentlyHovered = null;
		} else {
			//Handle the case where an iframe is hovered
		}
	};

	private async onMessage(event: MessageEvent) {
		if (!event.data || !event.source) {
			return;
		}
		const handler = this.messagesListeners.get(event.source);
		if (!handler) {
			return;
		}
		await handler.onMessage(event);
	};

	override async component_initialize() {
		await super.component_initialize();
		const modals = <ModalsZoneNS.Component>this.component_find('@.modals');
		if (!modals || !(modals instanceof ModalsZoneNS.Component)) {
			this.component_throwLibraryError('ModalsZone component not found!', {});
		}
		this.modals = modals;
	};

	sequential = (() => {
		const sequence: (() => Promise<unknown> | unknown)[] = [];
		return async function <TReturn = void>(
			this: ApplicationComponent<any, any>,
			fn: () => Promise<TReturn> | TReturn
		): Promise<TReturn> {
			sequence.push(fn);
			await STD.misc.poll(
				() => (sequence[0] === fn),
				5,
				15 * 1000
			);
			let r: TReturn;
			try {
				r = await fn();
			} catch (error: any) {
				sequence.shift();
				throw error;
			}
			sequence.shift();
			return r;
		};
	})();

	async onResize(ev: STD.events.Event<any, CM.Component<any, any>>) {
		const {newSize} = ev.details;
		const newRenderMode = (
			((newSize.x <= 500) || (newSize.y <= 500)) ? 'small' :
				((newSize.x <= 1400) && (newSize.y <= 1400)) ? 'medium' :
					'wide'
		);
		await (<any>ev.target).updateRenderMode(newRenderMode);
	};

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component> [ 0 ],
		configuration: TConfiguration
	) {
		Object.assign(
			{
				'@preventDefaultDisplayNone': true
			},
			configuration
		);
		super(descriptor, configuration);
		if (ApplicationComponent.count !== 0) {
			this.component_throwUserError('Only one application component is allowed!', {ApplicationComponent});
		}
		ApplicationComponent.count++;
		if (!(<any>window).app) {
			(<any>window).app = this;
		}
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({});
		window.addEventListener('message', this.onMessage.bind(this));
		window.addEventListener('mousemove', this.onMouseMove.bind(this), true);
		window.addEventListener('mouseout', this.onMouseOut.bind(this), true);
		/** Prevent unwanted dragging **/
		window.addEventListener('dragstart', (ev) => {
			ev.preventDefault();
		}, {capture: true, passive: false});
	};
};
export namespace NS {
	export const Component = ApplicationComponent;
	export type Component = ApplicationComponent<any, IApplicationComponentConfiguration>;
	export type Configuration = IApplicationComponentConfiguration;
}

CM.Manager.declare({
	id: 'application',
	type: ApplicationComponent
});

